import React from 'react';
import './UsageTiles.scss';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import { PRICING } from '../../../../shared/routes/path';
import { numberFormat } from '../../../../shared/utils/utils';

export type UsageTileKey = 'prompts' | 'team' | 'projects';

export interface UsageTileData {
  key: UsageTileKey;
  used: number;
  /** `null` = no limit on the account — the tile shows the count alone, without a bar. */
  max: number | null;
  /** i18n key of the line under the bar; interpolated with `noteOptions`. */
  noteKey?: string;
  noteOptions?: Record<string, number | string>;
  /** The pricing-page plan button, leading to `/pricing`. */
  upgradeCta?: boolean;
}

interface UsageTilesProps {
  tiles: UsageTileData[];
  /** Extra card(s) in the same grid — the hub appends the AI models tile. */
  children?: React.ReactNode;
}

// Label and icon per tile — labels reuse the panel's generic keys, no hub-specific copy needed.
const TILE_META: Record<UsageTileKey, { labelKey: string; icon: string }> = {
  prompts: { labelKey: 'prompts', icon: 'ico--message-square' },
  team: { labelKey: 'team', icon: 'ico--users' },
  projects: { labelKey: 'projects', icon: 'ico--folder' },
};

/**
 * The hub's three "X / Y in use" tiles (+ the models card passed as children). Frame 1:1 with the Insights KPI card, fill levels and the mini
 * progress bar shared with the Subscription usage tiles, the CTA = the project wizard's primary button
 * (`btn--xxl`: 8px radius, 46px, the base brand blue).
 */
const UsageTiles: React.FC<UsageTilesProps> = ({ tiles, children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="usage-tiles">
      {tiles.map((tile) => {
        const { labelKey, icon } = TILE_META[tile.key];
        const hasLimit = tile.max !== null;
        const overLimit = hasLimit && tile.used > (tile.max as number);
        const percent = hasLimit ? Math.min(100, (tile.used / (tile.max as number)) * 100) : 0;

        return (
          <div key={tile.key} className="usage-tiles__card">
            <div className="usage-tiles__label">
              <span className={`usage-tiles__icon ico--xs ${icon}`} />
              {t(labelKey)}
            </div>
            <div className={`usage-tiles__value${overLimit ? ' usage-tiles__value--danger' : ''}`}>
              {numberFormat().format(tile.used)}
              {hasLimit && (
                <span className="usage-tiles__max">/ {numberFormat().format(tile.max as number)}</span>
              )}
              <span className="usage-tiles__unit">{t('agencyHubInUse')}</span>
            </div>
            {hasLimit && (
              <ProgressBar
                className="usage-tiles__bar progress-bar progress-bar--mini progress-bar--animated progress-bar--primary"
                value={percent}
                showValue={false}
              />
            )}
            <div className="usage-tiles__subtitle">
              {tile.noteKey ? t(tile.noteKey, tile.noteOptions) : !hasLimit ? t('agencyHubNoLimit') : ''}
            </div>
            {tile.upgradeCta && (
              <Button
                className="btn btn--xxl usage-tiles__cta"
                label={t('agencyHubUpgradePlan')}
                onClick={() => navigate(PRICING)}
              />
            )}
          </div>
        );
      })}
      {children}
    </div>
  );
};

export default UsageTiles;
